import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaGripVertical, FaLayerGroup } from 'react-icons/fa';
import styles from './ArrangeSubjectsModal.module.css';

const ArrangeSubjectsModal = ({
  show,
  onHide,
  subjects = [],
  videoCountsBySubject = {},
  getSubjectConfig,
  onSave,
  isSaving = false,
}) => {
  const [orderedList, setOrderedList] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const scrollContainerRef = useRef(null);
  const autoScrollFrameRef = useRef(null);
  const autoScrollSpeedRef = useRef(0);
  const dragCloneRef = useRef(null);

  // Stop auto-scrolling and cancel animation frames
  const stopAutoScroll = useCallback(() => {
    autoScrollSpeedRef.current = 0;
    if (autoScrollFrameRef.current) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  // Continuous auto-scroller loop using requestAnimationFrame
  const stepAutoScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container && autoScrollSpeedRef.current !== 0) {
      container.scrollTop += autoScrollSpeedRef.current;
      autoScrollFrameRef.current = requestAnimationFrame(stepAutoScroll);
    } else {
      autoScrollFrameRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback((speed) => {
    autoScrollSpeedRef.current = speed;
    if (!autoScrollFrameRef.current) {
      autoScrollFrameRef.current = requestAnimationFrame(stepAutoScroll);
    }
  }, [stepAutoScroll]);

  const floatingCloneRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const cleanupDragClone = useCallback(() => {
    if (dragCloneRef.current) {
      if (document.body.contains(dragCloneRef.current)) {
        document.body.removeChild(dragCloneRef.current);
      }
      dragCloneRef.current = null;
    }
    if (floatingCloneRef.current) {
      if (document.body.contains(floatingCloneRef.current)) {
        document.body.removeChild(floatingCloneRef.current);
      }
      floatingCloneRef.current = null;
    }
  }, []);

  // Update floating custom preview position on drag / dragover
  const updateFloatingPosition = useCallback((clientX, clientY) => {
    if (!floatingCloneRef.current || clientX === 0 || clientY === 0) return;
    const x = clientX - dragOffsetRef.current.x;
    const y = clientY - dragOffsetRef.current.y;
    floatingCloneRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }, []);

  // Clean up animation frames and clone on unmount
  useEffect(() => {
    const handleWindowDragOver = (e) => {
      if (floatingCloneRef.current && e.clientX && e.clientY) {
        updateFloatingPosition(e.clientX, e.clientY);
      }
    };
    window.addEventListener('dragover', handleWindowDragOver);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      stopAutoScroll();
      cleanupDragClone();
    };
  }, [stopAutoScroll, cleanupDragClone, updateFloatingPosition]);

  // Initialize or reset temporary ordered subjects when modal opens or subjects change
  useEffect(() => {
    if (show) {
      setOrderedList([...subjects]);
      setDraggedIndex(null);
      setDragOverIndex(null);
    } else {
      stopAutoScroll();
      cleanupDragClone();
    }
  }, [show, subjects, stopAutoScroll, cleanupDragClone]);

  // Compute distance from top/bottom boundary of scrollContainer
  const handleContainerDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    updateFloatingPosition(e.clientX, e.clientY);

    const container = scrollContainerRef.current;
    if (!container || draggedIndex === null) return;

    const rect = container.getBoundingClientRect();
    const clientY = e.clientY;
    const edgeThreshold = 65; // pixels from edge to trigger auto-scroll
    const maxSpeed = 14;

    if (clientY >= rect.top && clientY <= rect.top + edgeThreshold) {
      // Near top edge -> scroll up (speed proportional to proximity)
      const ratio = 1 - (clientY - rect.top) / edgeThreshold;
      const speed = -Math.max(3, Math.round(ratio * maxSpeed));
      startAutoScroll(speed);
    } else if (clientY <= rect.bottom && clientY >= rect.bottom - edgeThreshold) {
      // Near bottom edge -> scroll down
      const ratio = 1 - (rect.bottom - clientY) / edgeThreshold;
      const speed = Math.max(3, Math.round(ratio * maxSpeed));
      startAutoScroll(speed);
    } else {
      // Middle zone -> stop scrolling
      stopAutoScroll();
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());

    cleanupDragClone();

    const rowEl = e.currentTarget;
    const rect = rowEl.getBoundingClientRect();
    const offsetX = Math.min(Math.max(20, e.clientX - rect.left), rect.width - 20);
    const offsetY = Math.min(Math.max(20, e.clientY - rect.top), rect.height - 20);
    dragOffsetRef.current = { x: offsetX, y: offsetY };

    // 1. Create a transparent dummy element for setDragImage so browser doesn't draw its own translucent ghost
    const dummy = document.createElement('div');
    dummy.style.width = '1px';
    dummy.style.height = '1px';
    dummy.style.opacity = '0';
    dummy.style.position = 'fixed';
    dummy.style.top = '-9999px';
    document.body.appendChild(dummy);
    dragCloneRef.current = dummy;
    if (e.dataTransfer.setDragImage) {
      e.dataTransfer.setDragImage(dummy, 0, 0);
    }

    // 2. Create the floating moving subject card with 100% full opacity, vivid colors, and rich shadow
    const floatingCard = rowEl.cloneNode(true);
    floatingCard.id = 'floating-drag-subject';
    floatingCard.style.position = 'fixed';
    floatingCard.style.top = '0';
    floatingCard.style.left = '0';
    floatingCard.style.width = `${rect.width}px`;
    floatingCard.style.boxSizing = 'border-box';
    floatingCard.style.opacity = '1';
    floatingCard.style.pointerEvents = 'none';
    floatingCard.style.zIndex = '999999';
    floatingCard.style.backgroundColor = '#ffffff';
    floatingCard.style.boxShadow = '0 16px 36px rgba(0, 0, 0, 0.16), 0 4px 12px rgba(0, 0, 0, 0.08)';
    floatingCard.style.transform = `translate3d(${e.clientX - offsetX}px, ${e.clientY - offsetY}px, 0) scale(1.02)`;
    floatingCard.style.transition = 'none';
    floatingCard.style.cursor = 'grabbing';

    document.body.appendChild(floatingCard);
    floatingCloneRef.current = floatingCard;

    requestAnimationFrame(() => {
      setDraggedIndex(index);
    });
  };

  const handleDrag = (e) => {
    if (e.clientX && e.clientY) {
      updateFloatingPosition(e.clientX, e.clientY);
    }
  };

  const handleItemDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    stopAutoScroll();
    cleanupDragClone();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const updated = [...orderedList];
    const [movedItem] = updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, movedItem);

    setOrderedList(updated);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    stopAutoScroll();
    cleanupDragClone();
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleClose = () => {
    if (isSaving) return;
    stopAutoScroll();
    cleanupDragClone();
    onHide();
  };

  const handleSave = () => {
    if (isSaving || orderedList.length === 0) return;
    stopAutoScroll();
    onSave(orderedList);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      dialogClassName={styles.modalDialog}
      contentClassName={styles.modalContent}
    >
      <div className={styles.modalHeader}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className={styles.headerTitleWrapper}>
            <h5 className={styles.modalTitle}>Arrange Subject Order</h5>
            <span className={styles.countPill}>
              {orderedList.length} {orderedList.length === 1 ? 'Subject' : 'Subjects'}
            </span>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
            disabled={isSaving}
          />
        </div>
      </div>

      <div className={styles.modalBody}>
        <div className={styles.instructionBox}>
          <p className={styles.instructionText}>
            Use the grip <FaGripVertical className={styles.instructionGrip} /> to drag subjects into your preferred order.
          </p>
          <span className={styles.dragTipBadge}>Drag to reorder</span>
        </div>

        {orderedList.length === 0 ? (
          <div className={styles.emptyState}>No subjects available to arrange.</div>
        ) : (
          <div
            ref={scrollContainerRef}
            className={styles.scrollContainer}
            onDragOver={handleContainerDragOver}
            onDragLeave={stopAutoScroll}
          >
            <div className={styles.dragList}>
              {orderedList.map((subject, index) => {
                const config = getSubjectConfig ? getSubjectConfig(subject) : null;
                const Icon = config?.icon || FaLayerGroup;
                const count = videoCountsBySubject[subject] || 0;
                const isDragging = draggedIndex === index;
                const isOver = dragOverIndex === index && draggedIndex !== index;

                // Subtle, restrained row accent: light pastel border-left matching the card theme
                const rowBorderLeftColor = config?.iconColor || '#6366f1';
                const iconBg = config?.iconBg || '#f1f5f9';
                const iconColor = config?.iconColor || '#475569';
                const badgeBg = config?.badgeBg || '#f1f5f9';
                const badgeColor = config?.badgeColor || '#334155';

                return (
                  <div
                    key={subject}
                    className={`${styles.dragRow} ${isDragging ? styles.isDragging : ''} ${isOver ? styles.isOver : ''}`}
                    style={{
                      borderLeftColor: rowBorderLeftColor,
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDrag={handleDrag}
                    onDragOver={(e) => handleItemDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <div
                      className={styles.dragHandle}
                      title="Drag to reorder"
                      aria-label="Drag handle"
                    >
                      <FaGripVertical />
                    </div>

                    <span className={styles.orderBadge}>{index + 1}</span>

                    <div
                      className={styles.subjectIconWrapper}
                      style={{ backgroundColor: iconBg, color: iconColor }}
                    >
                      <Icon />
                    </div>

                    <span className={styles.subjectName}>{subject}</span>

                    <span
                      className={styles.videoCountBadge}
                      style={{
                        backgroundColor: badgeBg,
                        color: badgeColor,
                        borderColor: config?.border || '#e2e8f0',
                      }}
                    >
                      {count} {count === 1 ? 'Video' : 'Videos'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className={styles.modalFooter}>
        <Button
          type="button"
          className={styles.cancelBtn}
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={isSaving || orderedList.length === 0}
        >
          {isSaving ? 'Saving...' : 'Save Order'}
        </Button>
      </div>
    </Modal>
  );
};

export default ArrangeSubjectsModal;
